import { useLoginWithOAuth, usePrivy } from '@privy-io/react-auth';
import { useEffect, useRef } from 'react';
import { DRIFT_CUSTOM_WALLET_OPTIONS } from 'src/constants/wallets';
import useCurrentWalletAdapter from 'src/hooks/useCurrentWalletAdapter';
import useCurrentWalletConnectionState from 'src/hooks/useCurrentWalletConnectionState';
import useDriftClient from 'src/hooks/useDriftClient';
import useConnectCustomWallet from 'src/hooks/wallet-management/useConnectCustomWallet';
import { usePrivyWalletContext } from 'src/providers/wallets/PrivyWalletProvider';
import useFeatureFlagStore from 'src/stores/featureFlag/useFeatureFlagStore';

export default function useRestorePrivyNativeSession() {
	const isPrivyNativeAccountsEnabled = useFeatureFlagStore((s) =>
		s.flagEnabled('ENABLE_PRIVY_NATIVE_ACCOUNTS')
	);
	const { ready, authenticated } = usePrivy();
	const driftClient = useDriftClient();
	const currentWalletAdapter = useCurrentWalletAdapter();
	const { embeddedWallet, sessionMode } = usePrivyWalletContext();
	const { connectWithCustomWallet } = useConnectCustomWallet();
	const walletConnectionState = useCurrentWalletConnectionState();

	const hasAttemptedSessionRestoreRef = useRef(false);

	// Necessary to auto-connect OAuth sessions.
	useLoginWithOAuth();

	useEffect(() => {
		const isPrivyNativeSession = sessionMode === 'privy-native';

		const hasConnectedWalletAdapter =
			currentWalletAdapter.connected && !!currentWalletAdapter.publicKey;

		if (
			!isPrivyNativeAccountsEnabled ||
			!ready ||
			!authenticated ||
			!driftClient ||
			!embeddedWallet ||
			!isPrivyNativeSession ||
			hasConnectedWalletAdapter ||
			hasAttemptedSessionRestoreRef.current
		) {
			return;
		}

		hasAttemptedSessionRestoreRef.current = true;
		walletConnectionState.instance.update('Connecting', true);

		connectWithCustomWallet(
			DRIFT_CUSTOM_WALLET_OPTIONS.PRIVY_NATIVE_WALLET_ADAPTER_NAME,
			{ embeddedWallet }
		).catch((error) => {
			hasAttemptedSessionRestoreRef.current = false;
			walletConnectionState.instance.update('Connecting', false);
			console.error('Failed to restore Privy native session:', error);
		});
	}, [
		isPrivyNativeAccountsEnabled,
		ready,
		authenticated,
		driftClient,
		embeddedWallet,
		sessionMode,
		currentWalletAdapter.connected,
		currentWalletAdapter.publicKey,
		currentWalletAdapter.name,
		connectWithCustomWallet,
		walletConnectionState.instance,
	]);

	// Reset session restore attempt if the user is not authenticated anymore.
	useEffect(() => {
		if (authenticated) {
			return;
		}

		hasAttemptedSessionRestoreRef.current = false;
		walletConnectionState.instance.update('Connecting', false);
	}, [authenticated, walletConnectionState.instance]);
}
